
"use client"
import { useState, useEffect } from "react"
import { Plus, Trash2, Upload, X, CreditCard, Building, QrCode, Edit2, Save } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import axios from "axios"
import { API_ENDPOINTS } from "../../../constants/config"

export function PaymentMethodsManagement() {
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [editingId, setEditingId] = useState(null)

  // Form states for adding new payment method
  const [newMethod, setNewMethod] = useState({
    type: "upi",
    upiId: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountHolderName: "",
    branchName: "",
    qrCodeImage: null,
    isActive: true
  })

  useEffect(() => {
    fetchPaymentMethods()
  }, [])

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(API_ENDPOINTS.paymentMethods, {
        headers: { Authorization: `Bearer ${token}` }
      })
      console.log(response.data.data);
      setPaymentMethods(response.data.data || [])
    } catch (error) {
      console.error("Error fetching payment methods:", error)
      setMessage({ type: "error", text: "Failed to load payment methods" })
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = (e, methodId = null) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "Image size should be less than 5MB" })
        return
      }
      
      const reader = new FileReader()
      reader.onloadend = () => {
        if (methodId) {
          // Update existing method
          setPaymentMethods(prev =>
            prev.map(m => m._id === methodId ? { ...m, qrCodeImage: reader.result } : m)
          )
        } else {
          // For new method
          setNewMethod(prev => ({ ...prev, qrCodeImage: reader.result }))
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleAddMethod = async () => {
    if (newMethod.type === "upi" && !newMethod.upiId) {
      setMessage({ type: "error", text: "Please enter UPI ID" })
      return
    }
    if (newMethod.type === "bank" && (!newMethod.bankName || !newMethod.accountNumber || !newMethod.ifscCode)) {
      setMessage({ type: "error", text: "Please fill all bank details" })
      return
    }

    setSaving(true)
    try {
      const token = localStorage.getItem("token")
      const response = await axios.post(
        API_ENDPOINTS.paymentMethods,
        newMethod,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setPaymentMethods(prev => [...prev, response.data.data])
      setNewMethod({
        type: "upi",
        upiId: "",
        bankName: "",
        accountNumber: "",
        ifscCode: "",
        accountHolderName: "",
        branchName: "",
        qrCodeImage: null,
        isActive: true
      })
      setMessage({ type: "success", text: "Payment method added successfully" })
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to add payment method" })
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateMethod = async (methodId) => {
    setSaving(true)
    try {
      const token = localStorage.getItem("token")
      const method = paymentMethods.find(m => m._id === methodId)
      const response = await axios.put(
        API_ENDPOINTS.paymentMethod(methodId),
        method,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setPaymentMethods(prev =>
        prev.map(m => m._id === methodId ? response.data.data : m)
      )
      setEditingId(null)
      setMessage({ type: "success", text: "Payment method updated successfully" })
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to update payment method" })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteMethod = async (methodId) => {
    if (!window.confirm("Are you sure you want to delete this payment method?")) return

    setSaving(true)
    try {
      const token = localStorage.getItem("token")
      await axios.delete(API_ENDPOINTS.paymentMethod(methodId), {
        headers: { Authorization: `Bearer ${token}` }
      })
      setPaymentMethods(prev => prev.filter(m => m._id !== methodId))
      setMessage({ type: "success", text: "Payment method deleted successfully" })
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to delete payment method" })
    } finally {
      setSaving(false)
    }
  }

  const toggleActiveStatus = async (methodId) => {
    const method = paymentMethods.find(m => m._id === methodId)
    setSaving(true)
    try {
      const token = localStorage.getItem("token")
      const response = await axios.put(
        API_ENDPOINTS.paymentMethod(methodId),
        { ...method, isActive: !method.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setPaymentMethods(prev =>
        prev.map(m => m._id === methodId ? response.data.data : m)
      )
      setMessage({ type: "success", text: `Payment method ${!method.isActive ? 'activated' : 'deactivated'}` })
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update status" })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-6">Loading payment methods...</div>
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure multiple payment options for fee collection including UPI, QR codes, and bank accounts
        </p>
      </div>

      {message && (
        <Alert className={message.type === "error" ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
          <AlertDescription className={message.type === "error" ? "text-red-800" : "text-green-800"}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Add New Payment Method */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add New Payment Method
          </CardTitle>
          <CardDescription>Add UPI ID, QR Code, or Bank Account details</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={newMethod.type} onValueChange={(v) => setNewMethod({ ...newMethod, type: v })}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="upi">UPI / QR Code</TabsTrigger>
              <TabsTrigger value="bank">Bank Account</TabsTrigger>
            </TabsList>

            <TabsContent value="upi" className="space-y-4">
              <div>
                <Label htmlFor="upiId">UPI ID *</Label>
                <Input
                  id="upiId"
                  placeholder="example@upi"
                  value={newMethod.upiId}
                  onChange={(e) => setNewMethod({ ...newMethod, upiId: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="qrCode">Upload QR Code (Optional)</Label>
                <div className="mt-2 flex items-center gap-4">
                  <label htmlFor="qrCode" className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                      <p className="text-xs text-gray-500 mt-2">Click to upload</p>
                    </div>
                    <input
                      type="file"
                      id="qrCode"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e)}
                    />
                  </label>
                  {newMethod.qrCodeImage && (
                    <div className="relative">
                      <img
                        src={newMethod.qrCodeImage}
                        alt="QR Code Preview"
                        className="h-24 w-24 object-cover rounded-lg border"
                      />
                      <button
                        onClick={() => setNewMethod({ ...newMethod, qrCodeImage: null })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bank" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bankName">Bank Name *</Label>
                  <Input
                    id="bankName"
                    placeholder="Bank Name"
                    value={newMethod.bankName}
                    onChange={(e) => setNewMethod({ ...newMethod, bankName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="accountHolderName">Account Holder Name</Label>
                  <Input
                    id="accountHolderName"
                    placeholder="Account Holder"
                    value={newMethod.accountHolderName}
                    onChange={(e) => setNewMethod({ ...newMethod, accountHolderName: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="accountNumber">Account Number *</Label>
                  <Input
                    id="accountNumber"
                    placeholder="Account Number"
                    value={newMethod.accountNumber}
                    onChange={(e) => setNewMethod({ ...newMethod, accountNumber: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="ifscCode">IFSC Code *</Label>
                  <Input
                    id="ifscCode"
                    placeholder="IFSC Code"
                    value={newMethod.ifscCode}
                    onChange={(e) => setNewMethod({ ...newMethod, ifscCode: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="branchName">Branch Name</Label>
                <Input
                  id="branchName"
                  placeholder="Branch Name"
                  value={newMethod.branchName}
                  onChange={(e) => setNewMethod({ ...newMethod, branchName: e.target.value })}
                />
              </div>
            </TabsContent>
          </Tabs>

          <Button onClick={handleAddMethod} disabled={saving} className="mt-6">
            {saving ? "Adding..." : "Add Payment Method"}
          </Button>
        </CardContent>
      </Card>

      {/* Existing Payment Methods */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Configured Payment Methods</h3>
        {paymentMethods.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No payment methods configured yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {paymentMethods.map((method) => (
              <Card key={method._id} className={!method.isActive ? "opacity-60" : ""}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {method.type === "upi" ? (
                        <QrCode className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Building className="h-5 w-5 text-green-600" />
                      )}
                      <div>
                        <CardTitle className="text-base">
                          {method.type === "upi" ? "UPI Payment" : "Bank Transfer"}
                        </CardTitle>
                        <Badge variant={method.isActive ? "default" : "secondary"} className="mt-1">
                          {method.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingId(editingId === method._id ? null : method._id)}
                      >
                        {editingId === method._id ? <Save className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteMethod(method._id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {method.type === "upi" ? (
                    <>
                      <div>
                        <Label className="text-xs text-gray-500">UPI ID</Label>
                        {editingId === method._id ? (
                          <Input
                            value={method.upiId}
                            onChange={(e) => setPaymentMethods(prev =>
                              prev.map(m => m._id === method._id ? { ...m, upiId: e.target.value } : m)
                            )}
                            className="mt-1"
                          />
                        ) : (
                          <p className="font-mono text-sm">{method.upiId}</p>
                        )}
                      </div>
                      {method.qrCodeImage && (
                        <div>
                          <Label className="text-xs text-gray-500">QR Code</Label>
                          <img
                            src={method.qrCodeImage}
                            alt="Payment QR Code"
                            className="h-32 w-32 object-cover rounded-lg border mt-2"
                          />
                          {editingId === method._id && (
                            <label className="mt-2 inline-block">
                              <Button variant="outline" size="sm" asChild>
                                <span>
                                  <Upload className="h-3 w-3 mr-2" />
                                  Change QR Code
                                </span>
                              </Button>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, method._id)}
                              />
                            </label>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500">Bank Name</Label>
                          {editingId === method._id ? (
                            <Input
                              value={method.bankName}
                              onChange={(e) => setPaymentMethods(prev =>
                                prev.map(m => m._id === method._id ? { ...m, bankName: e.target.value } : m)
                              )}
                              className="mt-1"
                            />
                          ) : (
                            <p className="text-sm font-medium">{method.bankName}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">IFSC Code</Label>
                          {editingId === method._id ? (
                            <Input
                              value={method.ifscCode}
                              onChange={(e) => setPaymentMethods(prev =>
                                prev.map(m => m._id === method._id ? { ...m, ifscCode: e.target.value.toUpperCase() } : m)
                              )}
                              className="mt-1"
                            />
                          ) : (
                            <p className="text-sm font-mono">{method.ifscCode}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Account Number</Label>
                        {editingId === method._id ? (
                          <Input
                            value={method.accountNumber}
                            onChange={(e) => setPaymentMethods(prev =>
                              prev.map(m => m._id === method._id ? { ...m, accountNumber: e.target.value } : m)
                            )}
                            className="mt-1"
                          />
                        ) : (
                          <p className="text-sm font-mono">{method.accountNumber}</p>
                        )}
                      </div>
                      {method.accountHolderName && (
                        <div>
                          <Label className="text-xs text-gray-500">Account Holder</Label>
                          <p className="text-sm">{method.accountHolderName}</p>
                        </div>
                      )}
                      {method.branchName && (
                        <div>
                          <Label className="text-xs text-gray-500">Branch</Label>
                          <p className="text-sm">{method.branchName}</p>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="pt-3 border-t flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActiveStatus(method._id)}
                      disabled={saving}
                    >
                      {method.isActive ? "Deactivate" : "Activate"}
                    </Button>
                    {editingId === method._id && (
                      <Button
                        size="sm"
                        onClick={() => handleUpdateMethod(method._id)}
                        disabled={saving}
                      >
                        Save Changes
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
